// Armado del pedido al agente y control del loop de herramientas.
//
// Todo lo de acá es PURO (sin React, sin red) para poder testearlo: el hook
// solo lo usa. El endpoint es el mismo que usa el chat de V2 (`AGENT_CHAT_URL`
// → `/v1/agent/chat` del atlas-backend, o la edge function `chat-agent` si el
// flag `VITE_USE_ATLAS_BACKEND` está en "false": mismo contrato).

import { MODEL_SONNET } from '../pages/v2/agentModels';
import { contenidoUsuario } from './adjuntosChatAtlas';
import type {
  HerramientaChat,
  InterruptoresChat,
  MensajeApi,
  MensajeChatAtlas,
} from './tipos';

/** Modelo por defecto del chat. El backend valida contra su allow-list y cae
 *  a Sonnet si no lo reconoce. */
export const MODELO_POR_DEFECTO = MODEL_SONNET;

/**
 * Tope de rondas modelo↔herramientas por turno. Cada ronda es un POST más, así
 * que sin tope un modelo que se obsesiona con una tool puede quemar créditos
 * en loop. 6 alcanza de sobra para este chat (las tools no piden datos al
 * usuario: se resuelven solas).
 */
export const MAX_RONDAS_HERRAMIENTAS = 6;

export const INTERRUPTORES_POR_DEFECTO: InterruptoresChat = {
  // Apagada: los scrapers cuestan créditos de verdad. La prende el usuario.
  investigacion: false,
  busquedaWeb: true,
  respuestasGraficas: true,
};

// ── Historial → formato de la API ─────────────────────────────────────────

/**
 * Mapea los mensajes de la UI al historial que espera la API.
 * - Los avisos de error del cliente (`error: true`) NO viajan: no salieron del
 *   modelo y sólo confundirían el contexto.
 * - Los mensajes vacíos se descartan (la API rechaza content vacío).
 * - De un gráfico ya mostrado sólo va un marcador con el título: el HTML
 *   entero en cada turno multiplicaría el costo del contexto sin aportar nada.
 * - Un mensaje del usuario CON adjuntos viaja como array de bloques (imagen /
 *   texto del archivo + la consigna). Sin adjuntos sigue siendo el string
 *   pelado de siempre, así el caso normal no cambia de forma.
 */
export function historialParaApi(mensajes: MensajeChatAtlas[]): MensajeApi[] {
  const salida: MensajeApi[] = [];
  for (const m of mensajes) {
    if (m.error) continue;
    if (m.rol === 'user') {
      const contenido = contenidoUsuario(m.texto, m.adjuntos);
      // Un mensaje sin texto Y sin adjuntos no viaja: la API rechaza content
      // vacío. Con adjuntos, `contenidoUsuario` siempre devuelve bloques.
      if (Array.isArray(contenido) ? contenido.length > 0 : contenido)
        salida.push({ role: 'user', content: contenido });
      continue;
    }
    let texto = m.texto || '';
    // Una respuesta cortada viaja igual (es contexto real de lo que el usuario
    // leyó), pero AVISADA: si no, el modelo la lee como una frase suya que
    // terminó así a propósito.
    if (m.truncado && texto.trim()) {
      texto += '\n[registro: esta respuesta se cortó por un problema de conexión y quedó incompleta.]';
    }
    for (const g of m.graficos ?? []) {
      texto += `\n[registro: ya mostraste una respuesta gráfica titulada "${g.titulo}" — no la repitas.]`;
    }
    if (m.sugerencias && m.sugerencias.length > 0) {
      texto += `\n[registro: ofreciste estas acciones: ${m.sugerencias.join(' · ')}]`;
    }
    if (texto.trim()) salida.push({ role: 'assistant', content: texto });
  }
  return salida;
}

// ── Cuerpo del pedido ─────────────────────────────────────────────────────

export interface ArgsCuerpoPedido {
  mensajesApi: MensajeApi[];
  herramientas: HerramientaChat[];
  /** Bloque estable del system prompt (lo cachea el backend). */
  system: string;
  /** Bloque volátil (contexto de apertura, memorias): va después del cache. */
  systemVolatil?: string;
  interruptores: InterruptoresChat;
  modelo?: string;
  idCompany?: string | null;
}

/**
 * Cuerpo del POST a `AGENT_CHAT_URL`.
 *
 * MAPEO DE LOS INTERRUPTORES (verificado leyendo el backend —
 * atlas-backend/src/routes/agentChat.ts + services/agent/chatAgent.ts):
 *
 * • `busquedaWeb` → `web_search`. SOPORTADO: con `web_search:true` el backend
 *   antepone la tool server-side `web_search_20250305` (5 usos por turno).
 *
 * • `investigacion` → `mcp` + `research`. SOPORTADO PERO CON LETRA CHICA: el
 *   backend habilita el MCP de Apify (scrapers de Instagram/Facebook/Google
 *   Maps/e-commerce) sólo si `mcp:true` Y `research:true` Y tiene `APIFY_TOKEN`
 *   configurado (`useMcp = body.mcp === true && allowScraping && !!apifyToken`).
 *   Por eso mandamos los DOS flags juntos: `research` solo no habilita nada, y
 *   `mcp` solo queda gateado. Si el server no tiene el token, esto degrada EN
 *   SILENCIO (el turno corre sin scrapers y nadie avisa) — es una dependencia
 *   del backend, no algo que el front pueda garantizar.
 *   Ojo extra: con el modelo Gemini Flash, `research:true` hace que el backend
 *   ESCALE a Sonnet y cobre créditos. Este chat usa Sonnet por defecto, así que
 *   no aplica, pero si algún día se agrega el nivel "Baja", tenerlo en cuenta.
 *
 * • `respuestasGraficas` NO se manda: es del front (decide `tools`).
 *
 * `keepalive:true` pide los `: ping` cada 15s del SSE (el watchdog del hook los
 * usa para distinguir "pensando" de "conexión muerta"). No mandamos
 * `project_id` ni `agent_chat_id`: este chat no persiste en `agent_chats`
 * (ver ADR-2026-08-17-chat-atlas-motor-y-persistencia).
 */
export function construirCuerpoPedido(a: ArgsCuerpoPedido): Record<string, unknown> {
  const cuerpo: Record<string, unknown> = {
    messages: a.mensajesApi,
    tools: a.herramientas,
    system: a.system,
    model: a.modelo || MODELO_POR_DEFECTO,
    web_search: a.interruptores.busquedaWeb === true,
    mcp: a.interruptores.investigacion === true,
    research: a.interruptores.investigacion === true,
    keepalive: true,
  };
  if (a.systemVolatil) cuerpo.system_volatile = a.systemVolatil;
  // El backend lo ignora (el tenant sale del JWT), pero lo mandamos por paridad
  // con el resto de los clientes del endpoint.
  if (a.idCompany) cuerpo.id_company = a.idCompany;
  return cuerpo;
}

// ── Loop de herramientas ──────────────────────────────────────────────────

export interface EstadoRonda {
  /** `stop_reason` que devolvió el stream. */
  stopReason: string | null;
  /** Cuántas herramientas invocó el modelo en esta ronda. */
  herramientas: number;
  /** Índice de la ronda que acaba de terminar (0-based). */
  ronda: number;
}

/**
 * ¿Hay que pegarle otra vez al agente? Sólo si el modelo cortó pidiendo tools,
 * efectivamente pidió alguna, y todavía queda cupo de rondas. Devolver `false`
 * por cupo agotado NO es un error: el turno termina con lo que haya.
 */
export function debeSeguirElLoop(e: EstadoRonda): boolean {
  if (e.stopReason !== 'tool_use') return false;
  if (e.herramientas <= 0) return false;
  return e.ronda + 1 < MAX_RONDAS_HERRAMIENTAS;
}

/** true si el loop se cortó por tope (y no porque el modelo terminó). */
export function corteConLimiteAlcanzado(e: EstadoRonda): boolean {
  return (
    e.stopReason === 'tool_use' &&
    e.herramientas > 0 &&
    e.ronda + 1 >= MAX_RONDAS_HERRAMIENTAS
  );
}

/**
 * true si el stream terminó SIN que el modelo dijera por qué paró.
 *
 * El `stop_reason` llega en el `message_delta`, o sea el anteúltimo evento de
 * un turno sano. Si el stream cierra ORDENADAMENTE antes de eso (el backend
 * hace `raw.end()` en su `finally` cuando el proveedor falla o se estanca, y
 * cualquier proxy en el medio puede hacer lo mismo), el cliente ve un `done`
 * limpio: no hay excepción, no hay error 5xx y el turno terminaría en silencio
 * mostrando media respuesta como si estuviera completa. Este predicado es lo
 * que permite distinguir ese caso de un final de verdad.
 */
export function corteSinFinal(e: EstadoRonda): boolean {
  return e.stopReason === null;
}
